import React from "react";
import dayjs from "dayjs";
import {
  GridCellParams,
  GridColDef,
  GridFilterOperator,
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModel,
} from "@mui/x-data-grid";

import { DatePicker, DateTimePicker, TimePicker } from "@ui/datetime-selector";
import { Input } from "@ui/input";
import { Select } from "@ui/select";
import { SearchableSelect } from "@ui/select/searchable-select";
import { HideIcon } from "@/components/icons";

import { useAtomValue } from "jotai";
import {
  expressionMasterDataCacheAtom,
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import { useCodeHelper } from "@/hooks/useCode/useCodeHelper";
import { extractReferencedMasterDataTableNames } from "@/hooks/useCode/utils";
import { useGridApiContext } from "@mui/x-data-grid";
import apiCaller from "@/utils/api-caller";
import { parseFormData } from "@/utils/parser";

import {
  getDropdownDynamicDatasource,
  getDropdownOptions,
  getHeaderField,
  getRequiredError,
  isComputedHeader,
  isEmptyValue,
} from "./grid-data-grid.helpers";
import {
  GridHeaderItem,
  DropdownDynamicDatasource,
} from "./grid-data-grid.types";
import { useBpmDatasetRecords } from "@/hooks/useMasterData";
import { useDebounce } from "@/hooks/useDebounce";
import { useSubscribeField } from "@/hooks/useFormBuilder";
import type { SelectOption } from "@ui/select/single-select";

// useCodeHelper's getFormField reads live from the interpreter store atom at
// runtime, so formSchema/formData are only consulted when iStore is null
// (compile-time path). At cell render time iStore is always present, so
// stable empty stand-ins are safe and prevent useCodeHelper from recreating
// its internal callbacks every render — which was driving an infinite loop
// via RowExpressionCell's setEditCellValue effect.
const STABLE_CODE_HELPER_SCHEMA = { root: [] as string[], entities: {} };
const STABLE_CODE_HELPER_DATA: Record<string, unknown> = {};
const ROW_EXPRESSION_PENDING = Symbol("row_expression_pending");

const toComparableDate = (
  value: unknown,
  subtype?: GridHeaderItem["subtype"],
): Date | null => {
  if (value === undefined || value === null || value === "") return null;

  const timestamp =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number(value)
        : Number(value);

  if (Number.isNaN(timestamp)) return null;

  if (subtype === "time") {
    const normalized = dayjs(timestamp);
    return dayjs("2000-01-01")
      .hour(normalized.hour())
      .minute(normalized.minute())
      .second(normalized.second())
      .millisecond(normalized.millisecond())
      .toDate();
  }

  if (subtype === "date") {
    return dayjs(timestamp).startOf("day").toDate();
  }

  return dayjs(timestamp).toDate();
};

const getGridDateOperatorsBySubtype = (
  subtype?: GridHeaderItem["subtype"],
): GridFilterOperator[] =>
  getGridDateOperators(subtype !== "date").map((operator) => {
    const baseGetApply = operator.getApplyFilterFn;
    if (!baseGetApply) return operator;

    return {
      ...operator,
      getApplyFilterFn: (...args) => {
        const baseFn = baseGetApply(...args);
        if (!baseFn) return null;

        return (value, row, column, apiRef) => {
          const normalized = toComparableDate(value, subtype);
          if (normalized === null) {
            if (operator.value === "isEmpty") return true;
            if (operator.value === "isNotEmpty") return false;
            return false;
          }
          return baseFn(normalized, row, column, apiRef);
        };
      },
    };
  });

function RowExpressionCell({
  params,
  header,
}: {
  params: GridRenderEditCellParams;
  header: GridHeaderItem;
}) {
  const iStore = useAtomValue(interpreterStoreAtom);
  const expressionMasterDataCache = useAtomValue(expressionMasterDataCacheAtom);
  const runtimeApplication = useAtomValue(runtimeApplicationAtom);
  const { executeCode, getApplicantProfile } = useCodeHelper({
    formSchema: STABLE_CODE_HELPER_SCHEMA,
    formData: STABLE_CODE_HELPER_DATA,
    application: runtimeApplication,
  });

  // Stable ref so evaluateExpressionServer always reads the latest applicant
  // without needing to be in useCallback's deps.
  const selectedApplicant = useAtomValue(selectedApplicantAtom);

  const apiRef = useGridApiContext();
  const expressionCode = (header as any).expression?.trim() ?? "";
  const masterDataTableKeys = React.useMemo(
    () => extractReferencedMasterDataTableNames(expressionCode),
    [expressionCode],
  );

  const needsServerExecution = React.useMemo(() => {
    if (!expressionCode) return false;
    return (
      /\bfetch\s*\(/.test(expressionCode) || /\bawait\b/.test(expressionCode)
    );
  }, [expressionCode]);

  const evaluateExpressionLocal = React.useCallback(
    (row: any) => {
      if (!expressionCode) return "";
      if (
        masterDataTableKeys.some((tableKey) => {
          const status = expressionMasterDataCache[tableKey]?.status;
          return !status || status === "idle" || status === "loading";
        })
      ) {
        return ROW_EXPRESSION_PENDING;
      }
      try {
        const executable = executeCode(expressionCode);
        return typeof executable === "function"
          ? executable(row || {})
          : executable;
      } catch (error) {
        console.warn("Failed to evaluate row expression", error);
        return "";
      }
    },
    [
      executeCode,
      expressionCode,
      expressionMasterDataCache,
      masterDataTableKeys,
    ],
  );

  const evaluateExpressionServer = React.useCallback(
    async (row: any) => {
      if (!expressionCode || !iStore) {
        return "";
      }

      try {
        const rawValues = (iStore.getEntitiesValues?.() ?? {}) as Record<
          string,
          unknown
        >;
        const schema = iStore.schema ?? { root: [], entities: {} };
        const formData = parseFormData(rawValues, schema).data;

        // Give the backend the row data in addition to the form data
        const response = await apiCaller.post("/execution/fetch", {
          function: expressionCode,
          formData,
          rowData: row || {},
          serialNumber: runtimeApplication?.serialNumber,
          applicantId: selectedApplicant
            ? Number(selectedApplicant.id)
            : undefined,
        });

        return typeof response.data === "object"
          ? JSON.stringify(response.data)
          : String(response.data);
      } catch (error) {
        console.warn("Failed to evaluate row expression via server", error);
        return "";
      }
    },
    [expressionCode, iStore, runtimeApplication?.serialNumber, selectedApplicant],
  );

  React.useEffect(() => {
    let isMounted = true;
    let lastEditSnapshot = "";

    const updateValue = async () => {
      // In DataGrid v8, getRowWithUpdatedValues helps us read the current uncommitted edit values
      const currentRow =
        apiRef.current.getRowWithUpdatedValues(params.id, params.field) || {};

      let nextValue: unknown = "";

      if (needsServerExecution) {
        nextValue = await evaluateExpressionServer(currentRow);
      } else {
        nextValue = evaluateExpressionLocal(currentRow);
      }

      if (nextValue === ROW_EXPRESSION_PENDING) {
        return;
      }

      if (!isMounted) return;

      const nextStringValue =
        nextValue === undefined || nextValue === null ? "" : String(nextValue);

      // Use the newly fetched currentRow in case it changed during await
      const latestRow =
        apiRef.current.getRowWithUpdatedValues(params.id, params.field) || {};
      const currentSelfValue = latestRow[params.field];
      const currentStringValue =
        currentSelfValue === undefined || currentSelfValue === null
          ? ""
          : String(currentSelfValue);

      if (nextStringValue !== currentStringValue) {
        await apiRef.current.setEditCellValue({
          id: params.id,
          field: params.field,
          value: nextStringValue,
        });
      }
    };

    void updateValue();

    // Row-expression depends on other cells in the same row.
    // Re-evaluate as those edit values change so first commit shows immediately.
    const handleEditPropsChange = (eventParams: {
      id: GridRenderEditCellParams["id"];
      field: string;
    }) => {
      if (eventParams.id !== params.id) return;
      if (eventParams.field === params.field) return;
      void updateValue();
    };

    const unsubscribeCellEditPropsChange = (
      apiRef.current.subscribeEvent as (
        event: string,
        handler: (params: {
          id: GridRenderEditCellParams["id"];
          field: string;
        }) => void,
      ) => () => void
    )("cellEditPropsChange", handleEditPropsChange);

    const unsubscribeEditCellPropsChange = (
      apiRef.current.subscribeEvent as (
        event: string,
        handler: (params: {
          id: GridRenderEditCellParams["id"];
          field: string;
        }) => void,
      ) => () => void
    )("editCellPropsChange", handleEditPropsChange);

    // Re-evaluate whenever this row's edit-model changes (before row commit).
    const unsubscribeStateChange = apiRef.current.subscribeEvent(
      "stateChange",
      () => {
        const state = apiRef.current.state as {
          editRows?: Record<string, unknown>;
        };
        const rowState = state?.editRows?.[String(params.id)];
        const nextSnapshot = rowState ? JSON.stringify(rowState) : "";
        if (nextSnapshot === lastEditSnapshot) return;
        lastEditSnapshot = nextSnapshot;
        void updateValue();
      },
    );

    return () => {
      isMounted = false;
      unsubscribeCellEditPropsChange();
      unsubscribeEditCellPropsChange();
      unsubscribeStateChange();
    };
  }, [
    apiRef,
    params.id,
    params.field,
    evaluateExpressionLocal,
    evaluateExpressionServer,
    needsServerExecution,
  ]);

  const value =
    params.value === undefined || params.value === null
      ? ""
      : String(params.value);

  return (
    <Input
      disabled
      value={value}
      hasClearIcon={false}
      className="text-sm border-none rounded-none"
    />
  );
}

function DynamicDropdownCell({
  params,
  header,
  mode,
  disabled = false,
  readonly = false,
  className,
}: {
  params: GridRenderCellParams | GridRenderEditCellParams;
  header: GridHeaderItem;
  mode: "edit" | "view";
  disabled?: boolean;
  readonly?: boolean;
  className?: string;
}) {
  const dynamicDatasource = getDropdownDynamicDatasource(
    header.datasource,
  ) as DropdownDynamicDatasource;

  const tableKey = dynamicDatasource?.table?.tableKey?.trim() || undefined;
  const labelKey = dynamicDatasource?.table?.labelKey?.trim() || undefined;
  const valueKey = dynamicDatasource?.table?.valueKey?.trim() || undefined;
  const isDynamicReady = Boolean(tableKey && labelKey && valueKey);
  const searchInOptions = header.searchInOptions ?? false;
  const [optionSearchQuery, setOptionSearchQuery] = React.useState("");
  const debouncedOptionSearchQuery = useDebounce(optionSearchQuery, 300);
  const trimmedOptionSearchQuery = debouncedOptionSearchQuery.trim();

  const filter = dynamicDatasource?.filter;
  const isUiFilter = Boolean(filter && !filter.isReference);
  const isExpressionFilter = Boolean(filter && filter.isReference);

  // UI filter: subscribe to form field value
  const watchedFieldName =
    isUiFilter && filter && !filter.isReference
      ? filter.formField?.trim()
      : undefined;
  const watchedFieldValue = useSubscribeField(watchedFieldName);

  // Expression filter: need executeCode and row data
  const runtimeApplication = useAtomValue(runtimeApplicationAtom);
  const { executeCode } = useCodeHelper({
    formSchema: STABLE_CODE_HELPER_SCHEMA,
    formData: STABLE_CODE_HELPER_DATA,
    application: runtimeApplication,
  });

  const selectFields = React.useMemo(() => {
    if (!labelKey || !valueKey) return undefined;
    // For expression filter, fetch all columns so the expression can access any field
    if (isExpressionFilter) return undefined;
    // For UI filter, include the filter column
    const filterColumn =
      isUiFilter && filter && !filter.isReference
        ? filter.columnKey
        : undefined;
    const cols = new Set([labelKey, valueKey]);
    if (filterColumn) cols.add(filterColumn);
    return Array.from(cols);
  }, [labelKey, valueKey, isExpressionFilter, isUiFilter, filter]);

  const backendSearchParams = React.useMemo(() => {
    if (
      !searchInOptions ||
      !trimmedOptionSearchQuery ||
      !isDynamicReady ||
      !labelKey ||
      !valueKey
    ) {
      return undefined;
    }

    return Array.from(new Set([labelKey, valueKey])).reduce<
      Record<string, string>
    >((acc, key) => {
      acc[key] = trimmedOptionSearchQuery;
      return acc;
    }, {});
  }, [
    isDynamicReady,
    labelKey,
    searchInOptions,
    trimmedOptionSearchQuery,
    valueKey,
  ]);

  const { records, isLoading } = useBpmDatasetRecords(
    isDynamicReady && tableKey ? tableKey : undefined,
    {
      limit: 1000,
      ...(selectFields ? { select: selectFields } : {}),
      search: backendSearchParams,
      sortBy: dynamicDatasource?.sorter?.columnKey,
      sortOrder: dynamicDatasource?.sorter?.order,
    },
  );

  // Get current grid row data for expression filter
  const currentRow = React.useMemo(() => {
    if (!isExpressionFilter) return {};
    try {
      return params.api.getRowWithUpdatedValues(params.id, params.field) || {};
    } catch {
      return (params.row as Record<string, unknown>) || {};
    }
  }, [isExpressionFilter, params.api, params.id, params.field, params.row]);

  const options: SelectOption<string>[] = React.useMemo(() => {
    if (!isDynamicReady || !labelKey || !valueKey) return [];
    const seen = new Set<string>();
    const result: SelectOption<string>[] = [];

    (records as Record<string, unknown>[]).forEach((record) => {
      // Apply UI filter
      if (
        isUiFilter &&
        filter &&
        !filter.isReference &&
        filter.columnKey &&
        watchedFieldValue !== undefined &&
        watchedFieldValue !== null &&
        String(watchedFieldValue).trim() !== ""
      ) {
        const recordValue = record[filter.columnKey];
        if (!compareValues(filter.logic, recordValue, watchedFieldValue)) {
          return;
        }
      }

      // Apply expression filter
      if (
        isExpressionFilter &&
        filter &&
        filter.isReference &&
        filter.expression?.trim()
      ) {
        try {
          const executable = executeCode(filter.expression);
          const result =
            typeof executable === "function"
              ? executable(record, currentRow)
              : executable;
          if (!result) return;
        } catch {
          return;
        }
      }

      const val = String(record[valueKey] ?? "").trim();
      if (!val || seen.has(val)) return;
      seen.add(val);

      const rawLabel = String(record[labelKey] ?? "");
      result.push({
        label:
          dynamicDatasource?.displayOption === "label_value"
            ? `${rawLabel} (${val})`
            : rawLabel,
        value: val,
        key: val,
      });
    });
    return result;
  }, [
    records,
    isDynamicReady,
    labelKey,
    valueKey,
    isUiFilter,
    isExpressionFilter,
    filter,
    watchedFieldValue,
    executeCode,
    currentRow,
  ]);
  const optionCacheRef = React.useRef<Record<string, SelectOption<string>>>({});

  React.useEffect(() => {
    options.forEach((option) => {
      optionCacheRef.current[String(option.value).trim()] = option;
    });
  }, [options]);

  const dropdownValue =
    params.value === undefined || params.value === null
      ? undefined
      : String(params.value);
  const renderOptions = React.useMemo(() => {
    if (!dropdownValue) return options;
    if (options.some((option) => String(option.value) === dropdownValue)) {
      return options;
    }

    const cachedOption = optionCacheRef.current[dropdownValue.trim()];
    return cachedOption ? [cachedOption, ...options] : options;
  }, [dropdownValue, options]);

  if (mode === "view") {
    const rawValue = (params.row as Record<string, unknown>)[
      getHeaderField(header)
    ];
    if (
      (rawValue === undefined || rawValue === null || rawValue === "") &&
      header.placeholder
    ) {
      return (
        <span className={className || "text-secondary-text"}>
          {header.placeholder}
        </span>
      );
    }
    const option = renderOptions.find(
      (item) => item.value === String(rawValue ?? ""),
    );
    return (
      <span className={`font-regular text-sm ${className ?? ""}`.trim()}>
        {isLoading ? "Loading..." : (option?.label ?? String(rawValue ?? ""))}
      </span>
    );
  }

  if (searchInOptions) {
    return (
      <SearchableSelect
        mode="single"
        value={dropdownValue}
        options={renderOptions}
        placeholder={
          isLoading
            ? "Loading options..."
            : (header.placeholder ?? "Select option")
        }
        className="text-sm border-none"
        disabled={disabled}
        readonly={readonly}
        filterOptionsOnClient={false}
        onSearchChange={setOptionSearchQuery}
        isFetchingMore={isLoading}
        onChange={(nextValue) => {
          const normalized = nextValue ? String(nextValue) : "";
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: normalized,
          });
        }}
      />
    );
  }

  return (
    <Select
      mode="single"
      value={dropdownValue}
      options={renderOptions}
      placeholder={
        isLoading
          ? "Loading options..."
          : (header.placeholder ?? "Select option")
      }
      className="text-sm border-none"
      disabled={disabled}
      readonly={readonly}
      onChange={(nextValue) => {
        const normalized = nextValue ? String(nextValue) : "";
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: normalized,
        });
      }}
    />
  );
}

/** Operator comparison utility for UI filter */
function compareValues(
  operator: string | undefined,
  left: unknown,
  right: unknown,
): boolean {
  if (!operator) return true;
  const leftNum = Number(left);
  const rightNum = Number(right);
  const numeric = Number.isFinite(leftNum) && Number.isFinite(rightNum);
  switch (operator) {
    case ">":
      return numeric ? leftNum > rightNum : String(left) > String(right);
    case "<":
      return numeric ? leftNum < rightNum : String(left) < String(right);
    case ">=":
      return numeric ? leftNum >= rightNum : String(left) >= String(right);
    case "<=":
      return numeric ? leftNum <= rightNum : String(left) <= String(right);
    case "==":
    case "equals":
      return String(left ?? "") === String(right ?? "");
    case "!=":
    case "not_equals":
      return String(left ?? "") !== String(right ?? "");
    case "contains":
      return String(left ?? "").includes(String(right ?? ""));
    case "not_contains":
      return !String(left ?? "").includes(String(right ?? ""));
    default:
      return true;
  }
}

export const createGridColumns = (
  headers: GridHeaderItem[],
  disabled = false,
  showHiddenColumnsPreview = false,
): GridColDef[] =>
  headers.map((header) => {
    const fieldKey = getHeaderField(header);
    const dropdownOptions = getDropdownOptions(header);
    const dynamicDatasource = getDropdownDynamicDatasource(header.datasource);
    const isTimeOnlyColumn =
      header.type === "date" && header.subtype === "time";
    const isHeaderReadonly = Boolean(header.readonly);
    const isHeaderDisabled = Boolean(header.disabled);
    const isHiddenColumn = showHiddenColumnsPreview && Boolean(header.hide);
    const isCellReadonly = isHeaderReadonly;
    const isCellDisabled = disabled || isHeaderDisabled;
    const isCellLocked = isCellReadonly || isCellDisabled;
    const isComputedColumn = isComputedHeader(header);

    const getRawValue = (row: GridRowModel) =>
      (row as Record<string, unknown>)[fieldKey];

    const toNumberOrNull = (value: unknown) => {
      if (value === undefined || value === null || value === "") return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const columnType: GridColDef["type"] =
      header.type === "number"
        ? "number"
        : header.type === "dropdown"
          ? "singleSelect"
          : "string";

    const filterOperators =
      header.type === "number"
        ? getGridNumericOperators()
        : header.type === "date"
          ? getGridDateOperatorsBySubtype(header.subtype)
          : header.type === "dropdown"
            ? getGridSingleSelectOperators()
            : getGridStringOperators();

    return {
      field: fieldKey,
      headerName: header.label,
      renderHeader: () => (
        <span
          className="inline-flex items-center gap-1.5"
          aria-required={header.required ? true : undefined}
        >
          <span className={isHiddenColumn ? "text-secondary-text" : undefined}>
            {header.label}
          </span>
          {isHiddenColumn && (
            <HideIcon
              className="h-[18px] w-[18px] text-secondary-text"
              aria-hidden="true"
            />
          )}
          {!isHiddenColumn && header.required && (
            <span className="p-1 text-red-500" aria-hidden="true">
              *
            </span>
          )}
        </span>
      ),
      type: columnType,
      width: 180,
      // Computed columns need to participate in the row edit pipeline so
      // their edit renderer can derive and push values, even though the user
      // should not treat them as directly editable inputs.
      editable: !isHiddenColumn && (isComputedColumn || !isCellLocked),
      resizable: true,
      hideable: false,
      minWidth: 140,
      filterable: !isTimeOnlyColumn,
      align: "left",
      headerAlign: "left",
      headerClassName: isHiddenColumn ? "grid-header-hidden" : undefined,
      filterOperators: isTimeOnlyColumn ? undefined : filterOperators,
      valueOptions: header.type === "dropdown" ? dropdownOptions : undefined,
      valueGetter: (_value, row) => {
        const rawValue = getRawValue(row as GridRowModel);
        if (header.type === "number") {
          return toNumberOrNull(rawValue);
        }
        if (header.type === "date") {
          return rawValue;
        }
        if (header.type === "dropdown") {
          return rawValue === undefined || rawValue === null
            ? ""
            : String(rawValue);
        }
        return rawValue === undefined || rawValue === null
          ? ""
          : String(rawValue);
      },
      valueParser: (value) => {
        if (header.type === "number") {
          return toNumberOrNull(value);
        }
        if (header.type === "date") {
          if (value === null || value === undefined || value === "") {
            return null;
          }
          if (typeof value === "number") return value;
          const parsed = dayjs(value as string).valueOf();
          return Number.isNaN(parsed) ? null : parsed;
        }
        if (header.type === "dropdown") {
          return value === undefined || value === null ? "" : String(value);
        }
        return value === undefined || value === null ? "" : String(value);
      },
      cellClassName: (params: GridCellParams) => {
        let valueForValidation = getRawValue(params.row as GridRowModel);

        try {
          const editRow = params.api.getRowWithUpdatedValues(
            params.id,
            params.field,
          ) as Record<string, unknown> | undefined;
          if (editRow && fieldKey in editRow) {
            valueForValidation = editRow[fieldKey];
          }
        } catch {
          // getRowWithUpdatedValues can throw when the row is not in edit mode.
        }

        const errorMessage = getRequiredError(header, valueForValidation);
        return [
          isHiddenColumn ? "grid-cell-hidden" : "",
          errorMessage ? "grid-cell-error" : "",
        ]
          .filter(Boolean)
          .join(" ");
      },
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const errorMessage = getRequiredError(header, params.props.value);
        const error = Boolean(errorMessage);
        return { ...params.props, error };
      },
      renderCell: (params: GridRenderCellParams) => {
        const value = getRawValue(params.row as GridRowModel);
        const textClassName = isHiddenColumn ? "text-secondary-text" : "";
        if (
          (header.type === "input" ||
            header.type === "number" ||
            header.type === "date" ||
            header.type === "dropdown" ||
            header.type) &&
          isEmptyValue(value) &&
          header.placeholder
        ) {
          return (
            <span className={textClassName || "text-secondary-text"}>
              {header.placeholder}
            </span>
          );
        }

        if (header.type === "date" && typeof value === "number") {
          const subtype = header.subtype ?? "date";
          const format =
            subtype === "time"
              ? "HH:mm"
              : subtype === "datetime"
                ? "YYYY-MM-DD HH:mm"
                : "YYYY-MM-DD";
          return (
            <span className={`font-regular text-sm ${textClassName}`.trim()}>
              {dayjs(value).format(format)}
            </span>
          );
        }

        if (header.type === "dropdown" && typeof value === "string") {
          if (dynamicDatasource) {
            return (
              <DynamicDropdownCell
                params={params}
                header={header}
                mode="view"
                disabled={isCellDisabled}
                readonly={isCellReadonly}
              />
            );
          }
          const option = dropdownOptions.find((item) => item.value === value);
          return (
            <span className={`font-regular text-sm ${textClassName}`.trim()}>
              {option?.label ?? value}
            </span>
          );
        }

        return (
          <span className={`font-regular text-sm ${textClassName}`.trim()}>
            {value as React.ReactNode}
          </span>
        );
      },
      renderEditCell: (params: GridRenderEditCellParams) => {
        const value =
          params.value === undefined || params.value === null
            ? ""
            : String(params.value);

        if (header.type === "row-expression") {
          return <RowExpressionCell params={params} header={header} />;
        }

        if (header.type === "date") {
          const subtype = header.subtype ?? "date";
          const timestamp =
            typeof params.value === "number"
              ? params.value
              : typeof params.value === "string" && params.value.trim()
                ? Number(params.value)
                : undefined;
          const safeTimestamp =
            typeof timestamp === "number" && !Number.isNaN(timestamp)
              ? timestamp
              : undefined;

          const handleDateChange = (nextValue?: number) => {
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: nextValue ?? null,
            });
          };

          const commonProps = {
            name: `grid-${String(params.id)}-${params.field}`,
            value: safeTimestamp,
            onChange: handleDateChange,
            placeholder: header.placeholder,
            required: header.required,
            disabled: isCellDisabled,
            readonly: isCellReadonly,
            usePortal: true,
          };

          if (subtype === "time") {
            return (
              <TimePicker
                {...commonProps}
                className="text-sm border-none"
                clearIcon
              />
            );
          }
          if (subtype === "datetime") {
            return (
              <DateTimePicker
                {...commonProps}
                className="text-sm border-none"
              />
            );
          }
          return (
            <DatePicker {...commonProps} className="text-sm border-none" />
          );
        }

        if (header.type === "dropdown") {
          if (dynamicDatasource) {
            return (
              <DynamicDropdownCell
                params={params}
                header={header}
                mode="edit"
                disabled={isCellDisabled}
                readonly={isCellReadonly}
              />
            );
          }

          const dropdownValue =
            params.value === undefined || params.value === null
              ? undefined
              : String(params.value);

          if (header.searchInOptions) {
            return (
              <SearchableSelect
                mode="single"
                value={dropdownValue}
                options={dropdownOptions}
                placeholder={header.placeholder ?? "Select option"}
                className="text-sm border-none"
                disabled={isCellDisabled}
                readonly={isCellReadonly}
                filterOptionsOnClient
                onChange={(nextValue) => {
                  const normalized = nextValue ? String(nextValue) : "";
                  params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: normalized,
                  });
                }}
              />
            );
          }

          return (
            <Select
              mode="single"
              value={dropdownValue}
              options={dropdownOptions}
              placeholder={header.placeholder ?? "Select option"}
              className="text-sm border-none"
              disabled={isCellDisabled}
              readonly={isCellReadonly}
              onChange={(nextValue) => {
                const normalized = nextValue ? String(nextValue) : "";
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: normalized,
                });
              }}
            />
          );
        }

        if (header.type === "number") {
          return (
            <Input
              type="number"
              autoFocus={params.hasFocus}
              value={value}
              placeholder={header.placeholder ?? ""}
              className="p-4 border-none text-sm font-regular"
              hasClearIcon={false}
              disabled={isCellDisabled}
              readOnly={isCellReadonly}
              onChange={(event) => {
                const rawValue = event.target.value;

                if (rawValue === "") {
                  params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: null,
                  });
                  return;
                }

                const parsed = Number(rawValue);
                if (Number.isNaN(parsed)) {
                  return;
                }

                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: parsed,
                });
              }}
            />
          );
        }

        if (header.type !== "input") {
          return (
            <Input
              autoFocus={params.hasFocus}
              hasClearIcon={false}
              value={value}
              placeholder={header.placeholder ?? ""}
              className="p-4 text-sm font-regular"
              disabled={isCellDisabled}
              readOnly={isCellReadonly}
              onChange={(event) => {
                const nextValue = event.target.value;
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: nextValue,
                });
              }}
            />
          );
        }

        return (
          <Input
            autoFocus={params.hasFocus}
            hasClearIcon={false}
            value={value}
            placeholder={header.placeholder ?? ""}
            className="p-4 text-sm font-regular border-none"
            disabled={isCellDisabled}
            readOnly={isCellReadonly}
            onChange={(event) => {
              const nextValue = event.target.value;
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: nextValue,
              });
            }}
          />
        );
      },
    };
  });
